import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router";
import { Box, Button, Container, TextField, Typography, Paper, Alert, Stack } from "@mui/material";
import axios from "axios";
import endpoints from "../api/endpoints";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState({ loading: false, error: [], success: null });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      setStatus({ loading: false, error: ["Missing reset token."], success: null });
      return;
    }
    if (!password || password !== confirmPassword) {
      setStatus({ loading: false, error: ["Passwords must match."], success: null });
      return;
    }
    if (password.length < 8) {
      setStatus({ loading: false, error: ["Password must be at least 8 characters."], success: null });
      return;
    }
    setStatus({ loading: true, error: null, success: null });
    try {
      await axios.post(endpoints.PASSWORD_RESET_CONFIRM, {
        token,
        password,
        password_confirm: password,
      });
      setStatus({ loading: false, error: [], success: "Password reset. Redirecting..." });
      setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      const data = err?.response?.data || {};
      const errors = []
        .concat(data.detail || [])
        .concat(data.message || [])
        .concat(data.password || [])
        .concat(data.non_field_errors || [])
        .filter(Boolean);
      setStatus({
        loading: false,
        error: errors.length ? errors : ["Unable to reset password. The link may be invalid or expired."],
        success: null,
      });
    }
  };

  return (
    <Box sx={{ backgroundColor: "#f7f9fc", minHeight: "100vh", py: { xs: 6, md: 10 } }}>
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, textAlign: "center" }}
        >
          <Typography variant="h5" fontWeight={800} gutterBottom>
            Reset your password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter a new password for your account. You'll be redirected after resetting.
          </Typography>

          {!token && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Missing or invalid reset token. Please request a new reset email.
            </Alert>
          )}

          {status.error && status.error.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Stack spacing={0.5}>
                {status.error.map((msg, idx) => (
                  <span key={idx}>{msg}</span>
                ))}
              </Stack>
            </Alert>
          )}
          {status.success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {status.success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label="New password"
                type="password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                helperText="Must be at least 8 characters."
              />
              <TextField
                label="Confirm password"
                type="password"
                fullWidth
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
              <Button variant="contained" type="submit" disabled={status.loading || !token}>
                {status.loading ? "Resetting..." : "Reset Password"}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
